import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Check, ClipboardList, MapPin, X } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { formatDateTime } from "../lib/format";

export default function AdminSwimReviewsPage() {
  const [workingId, setWorkingId] = useState<number | null>(null);
  const [actionError, setActionError] = useState("");

  const {
    data: reviews = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["admin_swim_reviews"],
    queryFn: () =>
      fetch("/app-api/admin/swim-reviews", {
        credentials: "include",
        cache: "no-store",
      }).then((response) => {
        if (!response.ok) throw new Error("Forbidden or unavailable");
        return response.json();
      }),
  });

  async function decide(id: number, action: "approve" | "reject") {
    if (workingId) return;
    setWorkingId(id);
    setActionError("");
    try {
      const response = await fetch(
        `/app-api/admin/swim-reviews/${id}/${action}`,
        { method: "POST" },
      );
      const data = await response.json();
      if (!response.ok)
        throw new Error(data.error || `Could not ${action} this peg`);
      await refetch();
    } catch (err) {
      setActionError(
        err instanceof Error ? err.message : "Could not update this submission",
      );
    } finally {
      setWorkingId(null);
    }
  }

  return (
    <div className="mx-auto max-w-[1200px] space-y-6 px-4 py-6 pb-28 md:py-8 md:pb-10">
      <header>
        <Link to="/admin" className="text-sm font-medium text-accent hover:underline">
          Admin
        </Link>
        <div className="mt-2 flex min-w-0 items-start gap-3">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-inset text-secondary">
            <MapPin />
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold tracking-tight text-primary">
              Peg Reviews
            </h1>
            <p className="mt-1 max-w-2xl text-sm text-secondary">
              Anglers can suggest missing pegs, but nothing becomes shared until you approve it.
            </p>
          </div>
        </div>
      </header>

      {actionError ? (
        <p className="rounded-lg bg-error-weak px-3 py-2 text-sm text-error" role="alert">
          {actionError}
        </p>
      ) : null}

      {isLoading ? (
        <div className="rounded-lg border border-border bg-raised p-6 text-center text-sm text-secondary">
          Loading peg submissions…
        </div>
      ) : error ? (
        <div className="rounded-lg border border-error bg-error-weak p-4 text-sm text-error">
          Could not load peg submissions.
          <button
            type="button"
            onClick={() => refetch()}
            className="ml-2 font-semibold underline"
          >
            Try again
          </button>
        </div>
      ) : reviews.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-8 text-center">
          <ClipboardList className="mx-auto text-secondary" />
          <p className="mt-3 text-sm text-secondary">No pegs are waiting for review.</p>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-secondary">
            {reviews.length} pending {reviews.length === 1 ? "peg" : "pegs"}
          </p>
          {reviews.map((review: any) => (
            <section
              key={review.id}
              className="rounded-xl border border-border bg-raised p-4"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="truncate font-semibold text-primary">{review.name}</h2>
                  <p className="mt-1 text-sm text-secondary">{review.lake_name}</p>
                  <p className="mt-1 text-xs text-tertiary">
                    Submitted by {review.submitted_by_name || "CatchCompass angler"} ·{" "}
                    {formatDateTime(review.created_at)}
                  </p>
                </div>
                <span className="shrink-0 rounded-full bg-warning-weak px-2 py-0.5 text-xs font-medium text-warning">
                  Pending
                </span>
              </div>

              {review.latitude != null && review.longitude != null ? (
                <div className="mt-3 rounded-lg bg-inset px-3 py-2 text-xs text-secondary">
                  <div className="flex items-center gap-1.5">
                    <MapPin className="size-3.5" />
                    <span>
                      GPS {Number(review.latitude).toFixed(6)},{" "}
                      {Number(review.longitude).toFixed(6)}
                    </span>
                  </div>
                  {review.accuracy_m != null ? (
                    <p className="mt-1">
                      Phone accuracy about {Math.round(Number(review.accuracy_m))} m
                    </p>
                  ) : null}
                  {review.captured_at ? (
                    <p className="mt-1">Captured {formatDateTime(review.captured_at)}</p>
                  ) : null}
                </div>
              ) : (
                <p className="mt-3 rounded-lg bg-inset px-3 py-2 text-xs text-secondary">
                  No GPS point was supplied. Approving will share the peg name only.
                </p>
              )}

              <div className="mt-4 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  disabled={workingId !== null}
                  onClick={() => decide(Number(review.id), "reject")}
                  className="flex min-h-11 items-center justify-center gap-2 rounded-lg border border-border px-3 py-2 text-sm font-semibold text-primary disabled:opacity-50"
                >
                  <X className="size-4" /> Reject
                </button>
                <button
                  type="button"
                  disabled={workingId !== null}
                  onClick={() => decide(Number(review.id), "approve")}
                  className="flex min-h-11 items-center justify-center gap-2 rounded-lg bg-accent px-3 py-2 text-sm font-bold text-white disabled:opacity-50"
                >
                  <Check className="size-4" /> Approve
                </button>
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
